import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import { eventService } from '../../../services/eventService';
import {
  PHASE_ADVANCE_OPTIONS,
  addDays,
  defaultPhaseDates,
  diffDays,
  getAdvanceHint,
  isEventDrivenAdvance,
  type PhaseAdvanceWhen,
} from './phasedPhaseUtils';

interface TicketRow {
  _id?: string;
  name: string;
  description?: string;
  price: number;
  quantity?: number;
}

interface PhaseDraft {
  name: string;
  startSaleDate: Date;
  endSaleDate: Date;
  advanceWhen: PhaseAdvanceWhen;
  prices: Record<string, number>;
  quantities: Record<string, number>;
}

interface ActivatePhasedWizardProps {
  open: boolean;
  eventId: string;
  tickets: TicketRow[];
  onClose: () => void;
  onSuccess: () => void;
}

const ActivatePhasedWizard: React.FC<ActivatePhasedWizardProps> = ({
  open,
  eventId,
  tickets,
  onClose,
  onSuccess,
}) => {
  const [step, setStep] = useState(0);
  const [phaseCount, setPhaseCount] = useState(2);
  const [phases, setPhases] = useState<PhaseDraft[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setStep(0);
    setPhaseCount(Math.min(2, Math.max(1, tickets.length > 0 ? 2 : 1)));
    setError(null);
  }, [open, tickets.length]);

  React.useEffect(() => {
    const draft: PhaseDraft[] = [];
    let prevEnd: Date | undefined;
    for (let i = 0; i < phaseCount; i++) {
      const prices: Record<string, number> = {};
      const quantities: Record<string, number> = {};
      tickets.forEach((t) => {
        const key = t._id || t.name;
        prices[key] = t.price ?? 0;
        quantities[key] = t.quantity ?? 100;
      });
      const { start, end } = defaultPhaseDates(prevEnd, 7);
      draft.push({
        name: `Lote ${i + 1}`,
        startSaleDate: start,
        endSaleDate: end,
        advanceWhen: i === 0 ? 'date' : 'previous_sold_out',
        prices,
        quantities,
      });
      prevEnd = end;
    }
    setPhases(draft);
  }, [phaseCount, tickets, open]);

  const updatePhase = (pi: number, patch: Partial<PhaseDraft>) => {
    setPhases((prev) => {
      const next = [...prev];
      next[pi] = { ...next[pi], ...patch };
      if (patch.endSaleDate && pi + 1 < next.length && next[pi + 1].advanceWhen === 'date') {
        next[pi + 1] = { ...next[pi + 1], startSaleDate: new Date(patch.endSaleDate) };
      }
      return next;
    });
  };

  const setDuration = (pi: number, days: number) => {
    const start = phases[pi]?.startSaleDate;
    if (!start || days < 1) return;
    updatePhase(pi, { endSaleDate: addDays(new Date(start), days) });
  };

  const handleConfirm = async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = {
        ticketTypes: tickets.map((t, i) => ({
          _id: t._id,
          name: t.name,
          description: t.description,
          sortOrder: i,
          isActive: true,
        })),
        salesPhases: phases.map((p, pi) => ({
          name: p.name,
          sortOrder: pi,
          startSaleDate: p.startSaleDate,
          endSaleDate: p.endSaleDate,
          advanceWhen: p.advanceWhen,
          offers: tickets.map((t) => {
            const key = t._id || t.name;
            return {
              _id: pi === 0 ? t._id : undefined,
              ticketTypeKey: t._id || t.name,
              price: p.prices[key] ?? 0,
              quantity: p.quantities[key] ?? 100,
            };
          }),
        })),
      };
      const res = await eventService.migrateToPhased(eventId, payload);
      if (!res.success) throw new Error(res.message || 'Erro na migração');
      onSuccess();
      onClose();
    } catch (e: unknown) {
      const apiMessage =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setError(apiMessage || (e instanceof Error ? e.message : 'Erro na migração'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !loading && onClose()} maxWidth="md" fullWidth>
      <DialogTitle>Ativar venda em lotes</DialogTitle>
      <DialogContent>
        <Stepper activeStep={step} sx={{ mb: 3, mt: 1 }}>
          <Step><StepLabel>Quantos lotes?</StepLabel></Step>
          <Step><StepLabel>Datas e regras</StepLabel></Step>
          <Step><StepLabel>Preços por lote</StepLabel></Step>
        </Stepper>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {step === 0 && (
          <Box>
            <Typography variant="body2" color="text.secondary" paragraph>
              Cada <strong>lote</strong> é uma fase de venda do evento. A partir do 2º lote, você escolhe
              se ele abre por data, quando esgotar o anterior, ou manualmente — sem precisar calcular horários.
            </Typography>
            <TextField
              select
              fullWidth
              label="Quantos lotes de venda?"
              value={phaseCount}
              onChange={(e) => setPhaseCount(Number(e.target.value))}
            >
              {[1, 2, 3, 4, 5].map((n) => (
                <MenuItem key={n} value={n}>{n} lote{n > 1 ? 's' : ''}</MenuItem>
              ))}
            </TextField>
          </Box>
        )}

        {step === 1 && (
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {phases.map((phase, pi) => {
                const eventDriven = pi > 0 && isEventDrivenAdvance(phase.advanceWhen);
                const prevName = pi > 0 ? phases[pi - 1].name : undefined;
                const duration = diffDays(phase.startSaleDate, phase.endSaleDate);
                return (
                  <Box key={pi} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>{phase.name}</Typography>

                    {pi > 0 && (
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label="Abrir este lote quando"
                        value={phase.advanceWhen}
                        sx={{ mb: 2 }}
                        onChange={(e) => {
                          const mode = e.target.value as PhaseAdvanceWhen;
                          const patch: Partial<PhaseDraft> = { advanceWhen: mode };
                          if (mode === 'date' && phases[pi - 1]?.endSaleDate) {
                            patch.startSaleDate = new Date(phases[pi - 1].endSaleDate);
                          }
                          updatePhase(pi, patch);
                        }}
                      >
                        {PHASE_ADVANCE_OPTIONS.map((o) => (
                          <MenuItem key={o.value} value={o.value}>
                            <Box>
                              <Typography variant="body2">{o.label}</Typography>
                              <Typography variant="caption" color="text.secondary">{o.hint}</Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </TextField>
                    )}

                    {eventDriven && (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        {getAdvanceHint(phase.advanceWhen, prevName)}
                      </Alert>
                    )}

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Nome do lote"
                          value={phase.name}
                          onChange={(e) => updatePhase(pi, { name: e.target.value })}
                        />
                      </Grid>

                      {(pi === 0 || phase.advanceWhen === 'date') && (
                        <Grid item xs={12} md={4}>
                          <DateTimePicker
                            label={pi === 0 ? 'Início das vendas' : 'Abre em (data)'}
                            value={phase.startSaleDate}
                            onChange={(d) => {
                              if (!d) return;
                              updatePhase(pi, { startSaleDate: d });
                              setDuration(pi, duration);
                            }}
                            slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                          />
                        </Grid>
                      )}

                      <Grid item xs={12} md={pi === 0 || phase.advanceWhen === 'date' ? 2 : 4}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="Duração (dias)"
                          value={duration}
                          inputProps={{ min: 1 }}
                          onChange={(e) => setDuration(pi, Number(e.target.value))}
                          helperText="Calcula o fim automaticamente"
                        />
                      </Grid>

                      <Grid item xs={12} md={pi === 0 || phase.advanceWhen === 'date' ? 4 : 8}>
                        <DateTimePicker
                          label="Fim das vendas"
                          value={phase.endSaleDate}
                          onChange={(d) => {
                            if (!d) return;
                            updatePhase(pi, { endSaleDate: d });
                          }}
                          slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                );
              })}
            </Box>
          </LocalizationProvider>
        )}

        {step === 2 && (
          <Box sx={{ overflowX: 'auto' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Defina preço e vagas de cada ingresso em cada lote.
            </Typography>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: 8 }}>Ingresso</th>
                  {phases.map((p, i) => (
                    <th key={i} style={{ textAlign: 'left', padding: 8 }}>{p.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tickets.map((t) => {
                  const key = t._id || t.name;
                  return (
                    <tr key={key}>
                      <td style={{ padding: 8, fontWeight: 600 }}>{t.name}</td>
                      {phases.map((p, pi) => (
                        <td key={pi} style={{ padding: 8 }}>
                          <TextField
                            size="small"
                            type="number"
                            label="R$"
                            value={p.prices[key] ?? 0}
                            onChange={(e) => {
                              const next = [...phases];
                              next[pi] = {
                                ...next[pi],
                                prices: { ...next[pi].prices, [key]: Number(e.target.value) },
                              };
                              setPhases(next);
                            }}
                            sx={{ width: 90, mr: 1 }}
                          />
                          <TextField
                            size="small"
                            type="number"
                            label="Vagas"
                            value={p.quantities[key] ?? 100}
                            onChange={(e) => {
                              const next = [...phases];
                              next[pi] = {
                                ...next[pi],
                                quantities: { ...next[pi].quantities, [key]: Number(e.target.value) },
                              };
                              setPhases(next);
                            }}
                            sx={{ width: 90 }}
                          />
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancelar</Button>
        {step > 0 && (
          <Button onClick={() => setStep((s) => s - 1)} disabled={loading}>Voltar</Button>
        )}
        {step < 2 ? (
          <Button variant="contained" onClick={() => setStep((s) => s + 1)}>Continuar</Button>
        ) : (
          <Button variant="contained" onClick={handleConfirm} disabled={loading}>
            {loading ? 'Ativando…' : 'Confirmar e ativar lotes'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ActivatePhasedWizard;
